import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Label } from "reactstrap";
import "../../assets/scss/custom/components/CustomSelector.scss";
import chevronDownIcon from "../../assets/images/support/chevron-down.svg";

/**
 * CustomSelector
 * Reusable dropdown selector styled to match the Omaha-style auth screens.
 *
 * Usage:
 * <CustomSelector
 *   label="Designation"
 *   name="designation_type"
 *   placeholder="Select designation"
 *   options={[
 *     { value: "manager", label: "Manager" },
 *     { value: "staff", label: "Staff" }
 *   ]}
 *   value={validation.values.designation_type}
 *   onChange={(value) => validation.setFieldValue("designation_type", value)}
 *   onBlur={validation.handleBlur}
 *   invalid={validation.touched.designation_type && !!validation.errors.designation_type}
 *   errorText={validation.errors.designation_type}
 * />
 */
const CustomSelector = ({
  label,
  name,
  id,
  placeholder,
  options = [],
  value,
  onChange,
  onBlur,
  invalid,
  errorText,
  optional,
  className,
  disabled,
  loading = false,
  ...rest
}) => {
  const selectorId = id || name;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSelect = (optionValue, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    }
    try {
      onChange(optionValue);
    } catch (error) {
      console.error("Error in CustomSelector handleSelect:", error);
    }
    setIsOpen(false);
    setSearchTerm("");
    if (onBlur) {
      try {
        onBlur();
      } catch (error) {
        console.error("Error in CustomSelector onBlur:", error);
      }
    }
  };

  const handleToggle = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!disabled && !loading) {
      try {
        setIsOpen(!isOpen);
        setSearchTerm("");
      } catch (error) {
        console.error("Error in CustomSelector handleToggle:", error);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  return (
    <div
      className={`custom-selector-wrapper ${className || ""}`}
      ref={dropdownRef}
    >
      <div
        className={`custom-selector-group ${invalid ? "is-invalid" : ""} ${disabled ? "is-disabled" : ""}`}
      >
        {label && (
          <Label htmlFor={selectorId} className="custom-selector-label">
            {label}
            {optional && (
              <span className="custom-selector-optional"> (Optional)</span>
            )}
          </Label>
        )}
        <div
          className={`custom-selector-field ${isOpen ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${selectorId}-dropdown`}
          aria-disabled={disabled}
        >
          <div className="custom-selector-value">
            {selectedOption ? (
              selectedOption.label
            ) : (
              <span className="custom-selector-placeholder">{placeholder}</span>
            )}
          </div>
          <div className="custom-selector-icon">
            {loading ? (
              <div className="custom-selector-spinner" />
            ) : (
              <img
                src={chevronDownIcon}
                alt="Toggle dropdown"
                className={isOpen ? "rotate-180" : ""}
              />
            )}
          </div>
        </div>
        {isOpen && !disabled && (
          <div
            className="custom-selector-dropdown"
            role="listbox"
            id={`${selectorId}-dropdown`}
          >
            {options.length > 5 && (
              <div className="custom-selector-search">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
              </div>
            )}
            {filteredOptions.length === 0 ? (
              <div className="custom-selector-no-results">No options found</div>
            ) : (
              <div className="custom-selector-options">
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`custom-selector-option ${option.value === value ? "is-selected" : ""}`}
                    onClick={(e) => handleSelect(option.value, e)}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {invalid && errorText && (
        <div className="custom-selector-feedback show">{errorText}</div>
      )}
    </div>
  );
};

CustomSelector.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  invalid: PropTypes.bool,
  errorText: PropTypes.string,
  optional: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
};

export default CustomSelector;
